import { isAbortError, isTimeoutError } from 'api/requestUtils'
import delay from 'lib/utils/delay'

/**
 * How long to wait before each re-attempt. Its length is also the retry count, so adding an entry
 * is the only change needed to retry once more.
 *
 * Short on purpose: every caller so far is a prefetch whose result gates a link the customer is
 * looking at, so a long ladder would leave the page looking like the feature does not exist.
 */
const RETRY_DELAYS_MS = [500, 1500]

/**
 * Whether a failed request is worth sending again.
 *
 * A rejection from `api/requestUtils` is either the API's own parsed error body — an object with a
 * numeric `status` — or, when the request never reached a response at all, whatever the transport
 * threw. That second case is the offline/DNS/connection-reset one and has no `status`, which is why
 * a missing status counts as transient rather than being treated as unknown and given up on.
 *
 * Everything else the API answers with is a settled answer, not a blip: 404 (no PNR yet), 422
 * (provider does not sell this), 403 (region not allowed), 401 (already replayed once by the auth
 * refresh in `requestUtils`). Re-asking cannot change any of them.
 */
export function isTransientRequestError(error: unknown): boolean {
  // The caller's own deadline or cancellation. Retrying would ignore the very limit that fired.
  if (isAbortError(error) || isTimeoutError(error)) { return false }

  const status = (error as { status?: unknown } | undefined)?.status
  if (typeof status !== 'number') { return true }

  return status >= 500 || status === 429 || status === 408
}

/**
 * Sends a request, re-sending it while it fails for a reason that could pass on its own.
 *
 * Rejects with the LAST error, so a caller's existing error handling keeps seeing the shape it
 * already expects.
 */
export default async function retryTransientRequest<T>(send: () => Promise<T>): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await send()
    } catch(error) {
      if (attempt >= RETRY_DELAYS_MS.length || !isTransientRequestError(error)) { throw error }
      await delay(RETRY_DELAYS_MS[attempt])
    }
  }
}
